import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import React from 'react'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { useSnackbars } from '@assembly/hooks'
import { getApiErrorMsg } from '@assembly/utils'
import { SnackbarType } from '@assembly/contexts'
import { updateCandidate } from '@assembly/api/candidates'
import { Candidate } from './types'

const StyledTextField = styled(TextField)(() => ({
    margin: 0,
    marginBottom: '20px',
    width: '100%',
}))

type MatchScoreDialogProps = {
    open: boolean
    onClose: () => void
    candidate: Candidate | null
    applicationIndex: number
    onSuccess: (candidate: Candidate) => void
}

export default function MatchScoreDialog({
    open,
    onClose,
    candidate,
    applicationIndex,
    onSuccess,
}: MatchScoreDialogProps) {
    const [loading, setLoading] = React.useState(false)
    const [matchScore, setMatchScore] = React.useState<number>(0)
    const [matchScoreDetails, setMatchScoreDetails] = React.useState('')
    const { addAlert } = useSnackbars()

    React.useEffect(() => {
        if (open && candidate && candidate.applicationMetaData[applicationIndex]) {
            const application = candidate.applicationMetaData[applicationIndex]
            setMatchScore(application.matchScore)
            setMatchScoreDetails(application.matchScoreDetails || '')
        }
    }, [open, candidate, applicationIndex])

    const handleClose = () => {
        setMatchScore(0)
        setMatchScoreDetails('')
        onClose()
    }

    const handleClickSave = async () => {
        if (!candidate) return

        try {
            setLoading(true)
            const updatedApplicationMetaData = [...candidate.applicationMetaData]
            updatedApplicationMetaData[applicationIndex] = {
                ...updatedApplicationMetaData[applicationIndex],
                matchScore: Number(matchScore),
                matchScoreDetails,
            }

            const { data } = await updateCandidate({
                ...candidate,
                applicationMetaData: updatedApplicationMetaData,
            })

            onSuccess(data)
            addAlert({
                message: 'Match score updated successfully',
                type: SnackbarType.Success,
            })
            handleClose()
        } catch (error: any) {
            addAlert({
                message: getApiErrorMsg(error),
                type: SnackbarType.Error,
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                Edit Match Score
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {candidate?.applicationMetaData[applicationIndex]?.jobTitle}
                </Typography>
                <StyledTextField
                    type="number"
                    label="Match Score"
                    value={matchScore}
                    onChange={(e) => setMatchScore(Number(e.target.value))}
                    inputProps={{ min: 0, max: 100 }}
                />
                <StyledTextField
                    label="Match Score Details"
                    value={matchScoreDetails}
                    onChange={(e) => setMatchScoreDetails(e.target.value)}
                    multiline
                    rows={10}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <LoadingButton loading={loading} onClick={handleClickSave}>
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
} 